import { deskToolHeadline } from "@cleo/shared/deskToolWords";
import { MEMBERS_CARD_TOUCH_ID } from "@cleo/shared/memberChangeCard";
/** W-786fe139: both conversation paths give the same Members-card explanation. */
export { MEMBERS_CARD_TOUCH_ID } from "@cleo/shared/memberChangeCard";
import { CARD_APPROVED_GATE_KEYS, type ApprovalCall } from "@cleo/contracts";
import {
  approvalCallIncomplete,
  approvalCallRequestText,
  approvalCallWillRun,
  REQUEST_NOT_SHOWN,
} from "@cleo/shared/approvalCall";
import type { PendingApproval } from "../../session-logic";
import { memberChangeKindNeedsTouchId } from "~/neucleos/memberChangeClient";
import {
  APPLY_MEMBER_CHANGE_TOOL,
  APPROVAL_READING_WORDS,
  calledTool,
  deskOwnToolCall,
  describedApprovalRequest,
  MEMBER_CHANGE_UNAVAILABLE,
  subscriptionApprovalBlocked,
  SUBSCRIPTION_FACTS_UNAVAILABLE,
  SUBSCRIPTION_SCHEDULE_TOOL,
  type ApprovalRequestContext,
} from "~/neucleos/gateInstancesClient";

/**
 * How a pending approval reads in the composer: the effect first, in the
 * person's words, then the consequences, with the complete request kept
 * verbatim underneath as the authority.
 *
 * W-dc12e842: what the card says comes from the request's structured identity
 * (PendingApproval.call: its kind, and for a tool call its server and tool,
 * recorded by the adapter from the engine's protocol), never from its text. A
 * Cleo action card, in plain words, is built only for a tool call of the
 * Desk's own server (deskOwnServer); its raw request stays folded under "Show
 * request". Every other request reads as what Approve will run ("Run a command
 * on your Mac", "Change files on your Mac: …", "Give Cleo's engine more
 * access", "Use <server>'s tool <tool>") with the raw request shown in full. A
 * request with no structured identity (an older record) reads generally, its
 * text shown in full.
 */

export const APPROVAL_HEADLINE_MAX = 140;

/**
 * The composer's disabled editor while an approval waits. The card above it
 * already shows the request, in plain words where it has them and verbatim
 * under "Show request", so the editor beside the decision buttons never
 * repeats it (it once echoed the raw `tool: {json}` a second time).
 */
export const APPROVAL_PENDING_PLACEHOLDER = "Resolve this approval request to continue";

export interface ApprovalPresentation {
  readonly kindLabel: "Action" | "File read" | "File change";
  readonly verb: "Run" | "Read" | "Apply";
  /** The harness tool that made the request, when the detail names one. */
  readonly tool: string | null;
  readonly headline: string;
  /** Consequence lines a governed request states before its blank line. */
  readonly supporting: readonly string[];
  /** True when the complete request says more than the headline shows. */
  readonly showRequest: boolean;
  /**
   * True when the headline and its supporting line say everything the request
   * carries, in the person's words (a folder or protected-place request: its
   * path and Cleo's reason). The verbatim request then starts folded under
   * "Show request", and nothing else on the card repeats it.
   */
  readonly plainWords: boolean;
  /**
   * W-6cc0d18a: the request itself, in the person's words, where the card
   * knows the tool (a folder or protected-place request: what Cleo will be
   * able to read, for how long, and why she asks). The card shows these lines
   * as its detail instead of the tool call; null shows the request verbatim.
   */
  readonly plainDetail: readonly string[] | null;
  /** W-d729ba9b: the exact changes behind plain words (a website's pages), under "Show exact changes". */
  readonly exactDetail?: readonly string[];
  /** Why Approve must not be possible right now (the facts are unread or refused), or null. */
  readonly approvalBlocked?: string | null;
  /** Decided for this exact call alone: the card offers no "always allow". */
  readonly perCallOnly?: boolean;
  /**
   * W-786fe139: a change to who belongs to a World that is approved with Touch ID: this card's Approve decides nothing
   * and opens the change's Members card in the Inbox, which asks for Touch ID. Decline stays here.
   */
  readonly approveOnCard?: boolean;
  /**
   * W-dc12e842: what will run, shown whole in monospace and never cut or folded: a command exactly as it will run,
   * or the arguments (the permissions) another tool or the engine asks for. Null when the card has none.
   */
  readonly code?: string | null;
  /**
   * W-dc12e842 (Codex pass 2): the complete request, offered under "Show request": the structured call with every
   * argument whole, or an older record's text. Never shortened; the view scrolls.
   */
  readonly request?: string;
}

const KIND: Record<
  PendingApproval["requestKind"],
  Pick<ApprovalPresentation, "kindLabel" | "verb"> & { readonly fallback: string }
> = {
  command: { kindLabel: "Action", verb: "Run", fallback: "Run an action" },
  "file-read": { kindLabel: "File read", verb: "Read", fallback: "Read a file" },
  "file-change": { kindLabel: "File change", verb: "Apply", fallback: "Change files" },
};

const stringField = (record: Record<string, unknown>, keys: readonly string[]) => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim().length > 0) return value.trim();
  }
  return null;
};

const shortened = (headline: string): string =>
  headline.length > APPROVAL_HEADLINE_MAX
    ? `${headline.slice(0, APPROVAL_HEADLINE_MAX - 1).trimEnd()}…`
    : headline;

/**
 * Cleo Can 35: a request to read a folder outside the person's chosen folders
 * reads as that decision, in plain words, around the request's own path, with
 * Cleo's reason beneath it. The complete request stays underneath as always.
 * W-dc12e842: only the Desk's own structured call (deskOwnToolCall) is this request.
 */
const FOLDER_REQUEST_TOOL = /^request_(?:folder|protected)_access$/;
function folderRequest(
  own: { readonly tool: string; readonly args: unknown } | null,
): { path: string; reason: string | null; protected: boolean } | null {
  if (own === null || !FOLDER_REQUEST_TOOL.test(own.tool)) return null;
  const args = own.args;
  if (typeof args !== "object" || args === null || Array.isArray(args)) return null;
  const record = args as Record<string, unknown>;
  const path = stringField(record, ["path"]);
  return path === null
    ? null
    : {
        path,
        reason: stringField(record, ["reason"]),
        protected: own.tool === "request_protected_access",
      };
}

/**
 * W-6cc0d18a: a folder or protected-place request, as the person reads it:
 * what Cleo will be able to read, for how long (the rest of this
 * conversation, threadFolderAccess.ts), and her own reason, with no tool name
 * or JSON. The approval lets her search and read; it grants no change.
 */
function folderRequestDetail(folder: {
  path: string;
  reason: string | null;
  protected: boolean;
}): readonly string[] {
  return [
    folder.protected
      ? `Cleo will be able to read ${folder.path}, a protected place that can hold keys, passwords or private data, for the rest of this conversation. This lets her read what is there, not change it. A new conversation asks again.`
      : `Cleo will be able to search and read the files in ${folder.path}, including the folders inside it, for the rest of this conversation. This lets her read what is there, not change it. A new conversation asks again.`,
    folder.reason === null ? "She gave no reason." : `Why she asks: “${folder.reason}”`,
  ];
}

/**
 * W-6082d348: a call the Inbox describes in plain words (describedApprovalRequest:
 * a message's people, World and exact text; a Canvas share's Canvas, audience,
 * rights and term) reads the same words on the card, one line per row. While
 * the names are read (reading), the card says so instead of general words.
 * W-dc12e842: only the Desk's own structured call has plain words, and its raw
 * request is always one click away under "Show request".
 */
function plainCall(
  request: { readonly detail: string; readonly call: ApprovalCall | null },
  named: { readonly context?: ApprovalRequestContext; readonly reading?: boolean },
): ApprovalPresentation | null {
  const plain = describedApprovalRequest(request, named.context ?? {}).plain;
  if (plain === null) return null;
  const headline = named.reading === true ? APPROVAL_READING_WORDS : plain.headline;
  return {
    kindLabel: "Action",
    verb: "Run",
    tool: null,
    headline: shortened(headline),
    supporting: [],
    showRequest: true,
    plainWords: true,
    plainDetail:
      named.reading === true
        ? []
        : plain.rows.map(([label, text]) => (label === "" ? text : `${label}: ${text}`)),
    ...(plain.approvalBlocked != null ? { approvalBlocked: plain.approvalBlocked } : {}),
    ...(plain.perCallOnly === true ? { perCallOnly: true } : {}),
    ...(named.reading !== true && plain.exact?.length ? { exactDetail: plain.exact } : {}),
  };
}

/** Gates approved only on the card of a stored change: their Approve also waits on the gate, whatever the call says. */
// The World's list (backend lib/gateCatalog.js CARD_APPROVED_GATE_KEYS), mirrored in @cleo/contracts under its drift test.
const CARD_ONLY_GATE_KEYS: ReadonlySet<string> = new Set(CARD_APPROVED_GATE_KEYS);

export function describeApproval(
  approval: Pick<PendingApproval, "requestKind" | "detail"> & {
    readonly call?: ApprovalCall | null;
    readonly gateKey?: string | null;
    readonly memberChangeKey?: string | null;
    readonly cardApproved?: boolean;
    /** Can 47: what the card says, read by the World when it opened the approval. */
    readonly cardFacts?: unknown;
  },
  given: { readonly context?: ApprovalRequestContext; readonly reading?: boolean } = {},
): ApprovalPresentation {
  // Can 47 / Can 68: the World's facts for this approval (the setting now, who asked) reach the conversation's card, as the Inbox's.
  const named =
    approval.cardFacts !== undefined && approval.cardFacts !== null
      ? { ...given, context: { ...(given.context ?? {}), cardFacts: approval.cardFacts } }
      : given;
  const described = describeCall(approval, named);
  // W-dc12e842 (fail closed): a card that cannot show the complete request for its kind offers only Decline
  // (@cleo/shared/approvalCall approvalCallIncomplete, the same rule Pocket and the Inbox follow).
  const shown: ApprovalPresentation = approvalCallIncomplete(approval.call)
    ? {
        ...described,
        approvalBlocked: described.approvalBlocked ?? REQUEST_NOT_SHOWN,
        perCallOnly: true,
      }
    : described;
  const gateKey = approval.gateKey ?? null;
  const memberChangeKey = approval.memberChangeKey ?? null;
  // The World's mark makes any gate card-only (the website pages card is keyed by its step, not its gate key).
  const cardApproved = approval.cardApproved === true;
  if (
    memberChangeKey === null &&
    !cardApproved &&
    (gateKey === null || !CARD_ONLY_GATE_KEYS.has(gateKey))
  )
    return shown;
  // A card-only gate (Can 73, Can 68) is approvable only when this card shows its stored change, even when the call
  // is missing, unreadable or names another tool.
  const request = { detail: (approval.detail ?? "").trim(), call: approval.call ?? null };
  const plain = describedApprovalRequest(request, named.context ?? {}).plain;
  // A gate the World marked as a member change's step waits on that change's card, whatever its own gate key.
  const why = subscriptionApprovalBlocked(gateKey, request, plain, memberChangeKey, cardApproved);
  // #1730: an invitation by link is approved on its card in the Inbox, whose Approve shows its link there once. This
  // conversation card's answer goes to the conversation's engine, which must never carry the link (the World refuses an
  // approval of it from here too); it can still be declined here.
  const read = named.context?.memberChange;
  // W-786fe139: a change that adds, removes or changes the role of a member is approved only with Touch ID on its
  // Members card (the World refuses any other approval of it, GATE_PRESENCE_REQUIRED), so this card's Approve opens that
  // card instead of sending an answer the World would refuse.
  const onCard =
    why === null && read?.status === "loaded" && memberChangeKindNeedsTouchId(read.change.kind);
  const link = onCard && read.change.kind === "invite-link";
  return !onCard && why === null
    ? shown
    : {
        ...shown,
        approvalBlocked:
          shown.approvalBlocked ?? why ?? (link ? INVITATION_LINK_IN_INBOX : MEMBERS_CARD_TOUCH_ID),
        perCallOnly: true,
        ...(onCard && shown.approvalBlocked == null ? { approveOnCard: true } : {}),
      };
}

/** #1730: why an invitation by link is not approved on the conversation's card. */
export const INVITATION_LINK_IN_INBOX =
  "To make this link, approve it in your Inbox. The link is shown there once.";
const quoted = (text: string): string => `“${text}”`;

/**
 * W-dc12e842: a request that is not the Desk's own tool call, as what Approve
 * will run, by its kind (@cleo/shared/approvalCall approvalCallWillRun): the
 * command exactly, each file's changes, the access asked for, or the other
 * server's tool and its arguments, whole. Never a Cleo card, whatever its text
 * looks like. A file change that does not carry its changes cannot be approved.
 */
function whatWillRun(
  approval: Pick<PendingApproval, "requestKind">,
  call: ApprovalCall,
): ApprovalPresentation {
  const kind = KIND[approval.requestKind];
  const words = approvalCallWillRun(call);
  const said =
    words.reason !== null && words.reason.trim() !== (words.code ?? "").trim()
      ? [quoted(words.reason)]
      : [];
  const labels =
    call.kind === "file-change"
      ? ({ kindLabel: "File change", verb: "Apply" } as const)
      : call.kind === "file-read"
        ? ({ kindLabel: "File read", verb: "Read" } as const)
        : call.kind === "permission"
          ? { kindLabel: kind.kindLabel, verb: kind.verb }
          : ({ kindLabel: "Action", verb: "Run" } as const);
  return {
    ...labels,
    tool: null,
    headline: shortened(words.headline),
    supporting: said,
    code: words.code,
    showRequest: true,
    plainWords: false,
    plainDetail: null,
  };
}

/**
 * W-dc12e842 (Codex pass 2): the complete request a card offers under "Show request": the structured call itself,
 * every argument whole (never the request line's shortened copy), or, for an older record, its text. It is offered
 * whenever it says more than the code block already shows whole.
 */
function withRequest(shown: ApprovalPresentation, request: string): ApprovalPresentation {
  return {
    ...shown,
    request,
    showRequest: request.length > 0 && (shown.plainWords || request !== (shown.code ?? null)),
  };
}

function describeCall(
  approval: Pick<PendingApproval, "requestKind" | "detail"> & {
    readonly call?: ApprovalCall | null;
  },
  named: { readonly context?: ApprovalRequestContext; readonly reading?: boolean } = {},
): ApprovalPresentation {
  const call = approval.call ?? null;
  return withRequest(
    describeCallWords(approval, named),
    call !== null ? approvalCallRequestText(call) : (approval.detail ?? "").trim(),
  );
}

function describeCallWords(
  approval: Pick<PendingApproval, "requestKind" | "detail"> & {
    readonly call?: ApprovalCall | null;
  },
  named: { readonly context?: ApprovalRequestContext; readonly reading?: boolean } = {},
): ApprovalPresentation {
  const kind = KIND[approval.requestKind];
  const detail = (approval.detail ?? "").trim();
  const call = approval.call ?? null;
  const own = deskOwnToolCall(call);
  // Not the Desk's own tool call: what Approve will run, by its kind.
  if (call !== null && own === null) return whatWillRun(approval, call);
  if (own === null) {
    // No structured identity (an older record): general words, the request shown whole.
    return {
      ...kind,
      tool: null,
      headline: kind.fallback,
      supporting: [],
      showRequest: detail.length > 0,
      plainWords: false,
      plainDetail: null,
    };
  }
  const folder = folderRequest(own);
  if (folder) {
    const plain = folder.protected
      ? `Let Cleo read ${folder.path}, which holds keys, passwords or private data, for this conversation`
      : `Let Cleo read ${folder.path} for this conversation`;
    return {
      kindLabel: "File read",
      verb: "Read",
      tool: null,
      headline: shortened(plain),
      // Cleo's reason is said once, in the detail below.
      supporting: [],
      showRequest: true,
      // The detail names the whole path, however long, so everything is said.
      plainWords: true,
      plainDetail: folderRequestDetail(folder),
    };
  }
  const plain = plainCall({ detail, call }, named);
  if (plain) return plain;
  // A subscription cancellation whose call could not be read: its facts are
  // unavailable, so the card says so and offers no way to approve it.
  if (own.tool === SUBSCRIPTION_SCHEDULE_TOOL) {
    return {
      ...kind,
      tool: own.tool,
      headline: "Stop a World's subscription renewing",
      supporting: [],
      showRequest: true,
      plainWords: false,
      plainDetail: null,
      approvalBlocked: SUBSCRIPTION_FACTS_UNAVAILABLE,
      perCallOnly: true,
    };
  }
  // Can 73: a change to who belongs to a World whose call could not be read is approved only on its card, which
  // cannot be shown, so the card says so and offers no way to approve it.
  if (calledTool(call) === APPLY_MEMBER_CHANGE_TOOL) {
    return {
      ...kind,
      tool: own.tool,
      headline: "Change who belongs to this World",
      supporting: [],
      showRequest: true,
      plainWords: false,
      plainDetail: null,
      approvalBlocked: MEMBER_CHANGE_UNAVAILABLE,
      perCallOnly: true,
    };
  }
  // Cleo's own tool, not described in plain words here: the World's preview of it where the runtime read one,
  // else its name, with the request shown whole.
  return {
    kindLabel: "Action",
    verb: "Run",
    tool: own.tool,
    headline: shortened(
      own.preview?.title ?? deskToolHeadline(own.tool, own.args) ?? `Let Cleo use ${own.tool}`,
    ),
    supporting: own.preview ? [...own.preview.lines] : [],
    showRequest: detail.length > 0,
    plainWords: false,
    plainDetail: null,
  };
}
